import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LoadingOverlay from '../components/LoadingOverlay';
import HelpButton from '../components/HelpButton';
import BotonVisualizar from '../components/BotonVisualizar';
import { usePageTour } from '../hooks/usePageTour';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle,
  Calendar,
  User,
  ArrowLeft,
  AlertTriangle,
  X,
  Eye,
  RefreshCw
} from 'lucide-react';
import { buildRechazoFormData } from '../utils/documentoEstadoUi';

const DocumentosPendientes = () => {
  const navigate = useNavigate();
  const [documentosPendientes, setDocumentosPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [documentoToReject, setDocumentoToReject] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [rechazando, setRechazando] = useState(false);

  usePageTour('pending-page', { enabled: !loading });

  useEffect(() => {
    cargarDocumentosPendientes();
  }, []);

  const cargarDocumentosPendientes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        toast.error('No estás autenticado');
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/firmas/pendientes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocumentosPendientes(data);
      } else {
        const errorData = await response.json();
        console.error('Error al cargar documentos pendientes:', errorData);
        if (response.status === 401) {
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente');
          navigate('/login');
        } else {
          toast.error('Error al cargar documentos pendientes');
        }
      }
    } catch (error) {
      console.error('Error al cargar documentos pendientes:', error);
      toast.error('Error al cargar documentos pendientes');
    } finally {
      setLoading(false);
      setUltimaActualizacion(new Date());
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/api/firmas/pendientes`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setDocumentosPendientes(await response.json());
        setUltimaActualizacion(new Date());
        toast.success('Documentos actualizados');
      } else {
        toast.error('Error al actualizar');
      }
    } catch (e) {
      toast.error('Error al actualizar: ' + e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const getEstadoIcon = (estado) => {
    switch ((estado || '').toLowerCase()) {
      case 'firmado':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rechazado':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getEstadoColor = (estado) => {
    switch ((estado || '').toLowerCase()) {
      case 'firmado':
        return 'bg-green-100 text-green-800';
      case 'rechazado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getEstadoTexto = (estado) => {
    switch ((estado || '').toLowerCase()) {
      case 'firmado':
        return 'Firmado';
      case 'rechazado':
        return 'Rechazado';
      default:
        return 'Pendiente';
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const firmarDocumento = (token) => {
    navigate(`/editor/${token}`);
  };

  const abrirModalRechazo = (token, titulo) => {
    setDocumentoToReject({ token, titulo });
    setMotivoRechazo('');
    setShowRejectModal(true);
  };

  const cerrarModalRechazo = () => {
    if (rechazando) return;
    setShowRejectModal(false);
    setDocumentoToReject(null);
    setMotivoRechazo('');
  };

  const rechazarDocumento = async () => {
    if (!documentoToReject || rechazando) return;

    try {
      const token_auth = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token_auth) {
        toast.error('No estás autenticado');
        navigate('/login');
        return;
      }

      setRechazando(true);
      const response = await fetch(`${API_BASE_URL}/api/firmar/${documentoToReject.token}/rechazar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token_auth}`
        },
        body: buildRechazoFormData(motivoRechazo)
      });

      if (response.ok) {
        toast.success('Documento rechazado exitosamente');
        setShowRejectModal(false);
        setDocumentoToReject(null);
        setMotivoRechazo('');
        cargarDocumentosPendientes();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Error al rechazar el documento');
      }
    } catch (error) {
      console.error('Error al rechazar documento:', error);
      toast.error('Error al rechazar el documento');
    } finally {
      setRechazando(false);
    }
  };

  // Cerrar modal con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showRejectModal && !rechazando) {
        cerrarModalRechazo();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showRejectModal, rechazando]);

  if (loading) {
    return <LoadingOverlay message="Cargando documentos pendientes..." />;
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background-color)'}}>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <div id="tour-pending-header" className="header-title">
              <h1>Mis Pendientes</h1>
              <p>Documentos que requieren tu firma</p>
            </div>
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {ultimaActualizacion && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)' }}>
                Actualizado: {ultimaActualizacion.toLocaleTimeString('es-CO', { timeStyle: 'short' })}
              </span>
            )}
            <button
              id="tour-pending-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Actualizar documentos pendientes"
              aria-busy={refreshing}
              title="Actualizar pendientes"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: '8px', fontSize: '0.8rem',
                fontWeight: 500,
                border: refreshing ? '1px solid #e5e7eb' : '1px solid #bfdbfe',
                background: refreshing ? '#f9fafb' : 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                color: refreshing ? '#9ca3af' : '#2563eb',
                transition: 'all 0.2s'
              }}
            >
              <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              <span>{refreshing ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Lista de documentos pendientes */}
        <div className="bg-white rounded-lg shadow-md">
          {documentosPendientes.length === 0 ? (
            <div id="tour-pending-empty" className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                <FileText className="h-12 w-12" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay documentos pendientes
              </h3>
              <p className="text-gray-500 mb-4">
                No tienes documentos que requieran tu firma en este momento.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {documentosPendientes.map((firma) => (
                <div key={firma.id} className="p-4 sm:p-6 hover:bg-gray-50">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                          {firma.documento?.titulo || 'Documento sin título'}
                        </h3>
                        <span className={`self-start sm:self-auto px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getEstadoColor(firma.estado)}`}>
                          {getEstadoTexto(firma.estado)}
                        </span>
                        {firma.tipo === 'recibido' && firma.requiere_orden_firma && (
                          <>
                            {firma.es_mi_turno ? (
                              <span className="self-start sm:self-auto px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-green-100 text-green-800">
                                Listo para firmar
                              </span>
                            ) : (
                              <span className="self-start sm:self-auto px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800">
                                En espera
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {firma.tipo === 'recibido' && firma.requiere_orden_firma && firma.total_firmantes != null && (
                        <p className="text-sm text-gray-600 mb-2">
                          Firmante {firma.orden_firmante ?? '?'} de {firma.total_firmantes}
                          {firma.en_espera && firma.nombre_esperando && (
                            <span className="text-amber-700"> · Esperando a: {firma.nombre_esperando}</span>
                          )}
                        </p>
                      )}
                      
                      {firma.documento?.descripcion && (
                        <p className="text-gray-600 mb-3">
                          {firma.documento.descripcion}
                        </p>
                      )}

                      {firma.documento?.owner_nombre && (
                        <p className="text-gray-600 mb-3">
                          <span className="font-medium">Enviado Por:</span> {firma.documento.owner_nombre}
                        </p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>
                            <span className="font-medium">Creado:</span> {formatFecha(firma.documento?.fecha_creacion)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>
                            <span className="font-medium">Firmante:</span> {firma.nombre} {firma.apellido}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getEstadoIcon(firma.estado)}
                          <span>
                            <span className="font-medium">Estado:</span> {getEstadoTexto(firma.estado)}
                          </span>
                        </div>
                        {firma.documento?.visualizadores && firma.documento.visualizadores.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <Eye className="h-4 w-4 text-blue-500" />
                            <span>
                              <span className="font-medium">Visualizadores:</span> {firma.documento.visualizadores.map(v => `${v.nombre} ${v.apellido}`).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>

                      {firma.comentarios && (
                        <div className="bg-gray-50 rounded-md p-3 mb-4">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Comentarios:</span> {firma.comentarios}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 sm:ml-4 mt-4 sm:mt-0 w-full sm:w-auto">
                      {(firma.estado || '').toLowerCase() === 'pendiente' && (
                        <>
                          <BotonVisualizar documentoId={firma.documento?.id} />
                          {firma.en_espera ? (
                            <button
                              disabled
                              title={firma.nombre_esperando ? `Esperando a que ${firma.nombre_esperando} firme primero` : 'Aún no es tu turno'}
                              className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-600 rounded-md cursor-not-allowed whitespace-nowrap"
                            >
                              En espera (no es tu turno)
                            </button>
                          ) : (
                            <button
                              onClick={() => firmarDocumento(firma.token_firma)}
                              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
                            >
                              Firmar Documento
                            </button>
                          )}
                          <button
                            onClick={() => abrirModalRechazo(firma.token_firma, firma.documento?.titulo || 'Documento')}
                            className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors whitespace-nowrap"
                          >
                            Rechazar
                          </button>
                        </>
                      )}
                      {(firma.estado || '').toLowerCase() === 'firmado' && firma.id && (
                        <button
                          onClick={() => {
                            const downloadUrl = `${API_BASE_URL}/api/download/${firma.id}`;
                            window.open(downloadUrl, '_blank');
                          }}
                          className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Descargar Documento Firmado
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación de rechazo */}
      {showRejectModal && documentoToReject && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={cerrarModalRechazo}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón cerrar */}
            <button
              type="button"
              onClick={cerrarModalRechazo}
              disabled={rechazando}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Icono de advertencia */}
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>

            {/* Título */}
            <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
              ¿Rechazar documento?
            </h3>

            {/* Mensaje */}
            <p className="text-gray-600 text-center mb-4">
              ¿Estás seguro de que deseas rechazar el documento{' '}
              <span className="font-semibold text-gray-900">
                "{documentoToReject.titulo}"
              </span>
              ?
            </p>

            <label className="block mb-6">
              <span className="block text-sm font-medium text-gray-700 mb-1.5">
                Motivo del rechazo (opcional)
              </span>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                disabled={rechazando}
                rows={3}
                maxLength={1000}
                placeholder="Describe el motivo si lo deseas"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </label>

            {/* Botones */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={cerrarModalRechazo}
                disabled={rechazando}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={rechazarDocumento}
                disabled={rechazando}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium disabled:opacity-60"
              >
                {rechazando ? 'Rechazando...' : 'Sí, rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <HelpButton tourId="pending-page" id="tour-pending-help" />
    </div>
  );
};

export default DocumentosPendientes;
